import { CSSProperties, ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { FaLock } from "react-icons/fa";
import useOutsideClick from "@core/hooks/useOutsideClick";
import "./FoodSidebarLock.scss";

type Props = {
    label: string;
    message: ReactNode;
    onContactUsClick: () => void;
};

/** Lock button shown next to a locked sidebar item; click toggles a notice with a "Contact us" request. */
const FoodSidebarLock: FCX<Props> = ({ className, testId, label, message, onContactUsClick }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isRequestSent, setIsRequestSent] = useState(false);
    // Distance from the start of the title row to the lock. The notice starts at the row's left edge (so it fits the
    // narrow sidebar even after a short title like "MAC") and its arrow is moved back under the lock.
    const [offset, setOffset] = useState(0);

    const close = useCallback(() => setIsOpen(false), []);
    useOutsideClick({ ref, isDisabled: !isOpen, fn: close });

    useLayoutEffect(() => {
        const row = ref.current?.parentElement;
        if (!isOpen || !ref.current || !row) return;
        setOffset(ref.current.getBoundingClientRect().left - row.getBoundingClientRect().left);
    }, [isOpen]);

    const handleContactUs = () => {
        onContactUsClick();
        setIsRequestSent(true);
    };

    return (
        <div ref={ref} className={cn("FoodSidebarLock", className)} data-testid={testId}>
            <button
                type="button"
                className="FoodSidebarLock__button"
                aria-label={label}
                aria-expanded={isOpen}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <FaLock aria-hidden />
            </button>
            {isOpen && (
                <div
                    role="status"
                    className="FoodSidebarLock__notice"
                    style={{ "--food-sidebar-lock-offset": `${offset}px` } as CSSProperties}
                >
                    {isRequestSent ? (
                        "Thank you for your request, we will get back to you shortly!"
                    ) : (
                        <>
                            {message}
                            <br />
                            <button type="button" className="FoodSidebarLock__contact" onClick={handleContactUs}>
                                Contact us
                            </button>{" "}
                            to learn more
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FoodSidebarLock;
